import { DisplayMode } from '@/api/generated/schemas';
import type { SectionRow } from '@/components/user_catalog/sections/sectionRows';
import { heroRowsToPublicationIds, type HeroLineupRow } from './heroLineup';

/** Live editor state of the hero tab, in the shape the comparison and the
 * preview both need. Owned by the page shell so it survives tab switches. */
export interface HeroDraft {
  name?: string | null;
  description?: string | null;
  publication_ids: number[];
}

export const heroDraftFromForm = (values: {
  name?: string | null;
  description?: string | null;
  slots?: (HeroLineupRow | undefined | null)[] | null;
}): HeroDraft => ({
  name: values.name,
  description: values.description,
  publication_ids: heroRowsToPublicationIds(values.slots),
});

/** One row of the sections tab: which collection, and how the client
 * presents it. */
export interface SectionDraftRow {
  collection_id: number;
  display_mode: DisplayMode;
}

/** Live editor state of the sections tab. Order carries the positions — the
 * server renumbers them from it on save. */
export interface SectionsDraft {
  rows: SectionDraftRow[];
}

export const sectionsDraftFromForm = (values: {
  sections?: (SectionRow | undefined | null)[] | null;
}): SectionsDraft => ({
  rows: (values.sections ?? [])
    .filter((row) => row?.collection_id != null)
    .map((row) => ({
      collection_id: row?.collection_id as number,
      // A row the editor never seeded saves as the server's own default.
      display_mode: row?.display_mode ?? DisplayMode.regular,
    })),
});

const text = (value: string | null | undefined) => value ?? '';

const sameOrder = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/** No draft yet (form not initialised) reads as clean. */
export const isHeroDirty = (
  draft: HeroDraft | undefined,
  server: HeroDraft
): boolean => {
  if (!draft) return false;
  if (text(draft.name) !== text(server.name)) return true;
  if (text(draft.description) !== text(server.description)) return true;
  return !sameOrder(draft.publication_ids, server.publication_ids);
};

/** No draft yet (form not initialised) reads as clean. Both what is ordered
 * and how each row displays are part of what a save writes, so a mode-only
 * change counts. */
export const isSectionsDirty = (
  draft: SectionsDraft | undefined,
  server: SectionsDraft
): boolean =>
  !!draft &&
  (draft.rows.length !== server.rows.length ||
    draft.rows.some(
      (row, i) =>
        row.collection_id !== server.rows[i]?.collection_id ||
        row.display_mode !== server.rows[i]?.display_mode
    ));
